import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { keyCodes } from '../../../common/constants';
import { DomUtils, fp as _, ReactUtils } from '../../utils';
import { CheckBox } from '../../Selections';
import { ArrowDownIcon } from '../../Icons';
import {
    InputFieldToggle,
    InputFieldControl,
    InputFieldGroup,
    InputFieldContainer,
    InputFieldLabel,
    InputFieldMessage
} from '../../InputBase';
import { Menu, MenuItem } from '../../Menu';
import DropdownInput from './DropdownInput';
import { getTextFromItem, getDataKeyItem, getDisplayValue, getSelectedValues } from '../utils';

class Dropdown extends Component {
    constructor(props) {
        super(props);

        this.isOpenControlled = props.open !== undefined;
        this.state = {
            isOpen: false,
            formattedValue: '',
            focused: false,
            autoFocus: false
        };

        this.optionId = `${props.id}_option`;
        this.listBoxId = `${props.id}_listBox`;

        this.anchorEl = React.createRef();

        this.handleToggle = this.handleToggle.bind(this);
        this.handleMenuClose = this.handleMenuClose.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    componentDidMount() {
        if (this.isOpenControlled && this.props.open) {
            this.forceUpdate();
        }
    }

    handleToggle(e) {
        const { isOpen } = this.state;
        const { disabled, onClick } = this.props;

        if (disabled) {
            return;
        }

        this.setState({ isOpen: !isOpen });

        if (onClick) {
            onClick(e);
        }
    }

    handleMenuClose(e) {
        const { onClose } = this.props;

        this.setState({ isOpen: false, autoFocus: false });

        if (onClose) {
            onClose(e);
        }
    }

    handleMenuItemEvent = (e, item) => {
        const { value: valueProps, defaultValue, dataItemKey, multiple, onChange, onClose } = this.props;

        if (!multiple) {
            this.setState({ isOpen: false });

            if (onClose) {
                onClose(e);
            }
        }

        if (onChange) {
            const currentValues = valueProps || defaultValue;
            const selectedValue = getDataKeyItem(item, dataItemKey);
            const result = multiple ? getSelectedValues(selectedValue, currentValues) : selectedValue;

            onChange(e, result, { item });
        }
    };

    handleMenuItemClick(item) {
        return e => this.handleMenuItemEvent(e, item);
    }

    handleMenuItemKeyDown(item) {
        const { multiple } = this.props;

        return e => {
            const { ENTER_KEY, SPACE, ESC } = keyCodes;
            const charCode = DomUtils.getCharCode(e);

            if (charCode === ESC) {
                e.stopPropagation();
            } else if (charCode === ENTER_KEY || (charCode === SPACE && multiple)) {
                e.preventDefault();
                e.stopPropagation();

                this.handleMenuItemEvent(e, item);
            }
        };
    }

    handleKeyDown(e) {
        const { ENTER_KEY, DOWN, TAB } = keyCodes;
        const charCode = DomUtils.getCharCode(e);

        if (charCode === ENTER_KEY) {
            e.preventDefault();
            this.handleToggle(e);
        } else if (charCode === DOWN) {
            e.preventDefault();
            this.setState({ autoFocus: true });
        } else if (charCode === TAB) {
            const { isOpen } = this.state;

            if (isOpen) {
                this.handleToggle(e);
            }
        }
    }

    handleKeyUp = e => {
        const charCode = DomUtils.getCharCode(e);

        if (charCode === keyCodes.ESC) {
            const { isOpen } = this.state;

            if (isOpen) {
                e.stopPropagation();

                this.handleMenuClose(e);
            }
        }
    };

    handleMenuKeyDown = e => {
        const { LEFT, RIGHT, UP, DOWN } = keyCodes;
        const charCode = DomUtils.getCharCode(e);

        if (charCode === UP || charCode === DOWN) {
            const { current } = this.anchorEl;

            if (this.state.isOpen) {
                const currentMenuItem = document.activeElement;

                current.setAttribute('aria-activedescendant', currentMenuItem.getAttribute('id'));
            } else {
                current.removeAttribute('aria-activedescendant');
            }
        }

        if (charCode === LEFT || charCode === RIGHT) {
            e.preventDefault();
            DomUtils.setFocus(this.anchorEl);
            this.setState({ autoFocus: false });
        }
    };

    handleMenuKeyUp = e => {
        const charCode = DomUtils.getCharCode(e);

        if (charCode === keyCodes.ESC) {
            e.preventDefault();
            e.stopPropagation();
            this.handleMenuClose(e);
        }
    };

    renderValue(displayText) {
        const { valueRender, multiple } = this.props;

        let defaulRender = getDisplayValue(displayText, multiple);

        if (valueRender) {
            defaulRender = valueRender({ ...this.props }, () => defaulRender);
        }

        return defaulRender;
    }

    renderItemText(item) {
        const { itemTextRender, ...other } = this.props;
        const { textField } = other;

        let defaulRender = getTextFromItem(item, textField);

        if (itemTextRender) {
            defaulRender = itemTextRender({ item, ...other }, () => defaulRender);
        }

        return defaulRender;
    }

    defaultItemRender(id, { item, multiple, selected, checked, onChange, onClick, onKeyDown, disabled }) {
        const { textField } = this.props;

        return (
            <MenuItem
                key={id}
                id={this.getOptionId(id)}
                role="option"
                selected={selected}
                disabled={disabled}
                onClick={onClick}
                onKeyDown={onKeyDown}
            >
                {multiple && (
                    <CheckBox
                        aria-label={getDataKeyItem(item, textField)}
                        className="mr-3"
                        checked={checked}
                        onChange={onChange}
                    />
                )}
                {this.renderItemText(item)}
            </MenuItem>
        );
    }

    renderItem(index, item, selected, multiple) {
        const { itemRender, dataItemKey } = this.props;
        const id = getDataKeyItem(item, dataItemKey);

        const menuItemProps = {
            selected: selected && !multiple,
            onClick: this.handleMenuItemClick(item),
            onKeyDown: this.handleMenuItemKeyDown(item)
        };
        const checkBoxProps = {
            checked: selected,
            onChange: this.handleMenuItemClick(item)
        };
        const itemRenderProps = {
            index,
            item,
            multiple,
            disabled: item.disabled || false,
            ...menuItemProps,
            ...checkBoxProps
        };

        if (itemRender) {
            return itemRender(itemRenderProps, this.defaultItemRender.bind(this));
        }

        return this.defaultItemRender(id, itemRenderProps);
    }

    getOptionId(index) {
        return `option-${this.optionId}-${index}`;
    }

    render() {
        const {
            classes,
            className,
            currentLength,
            data,
            dataItemKey,
            defaultValue,
            disabled,
            error,
            errorText,
            helpText,
            innerRef,
            label,
            maxLength,
            menuProps,
            multiple,
            name,
            open,
            placeholder,
            showToggle,
            styles,
            tabIndex,
            textField,
            value: valueProps,
            variant,
            toggleIcon: ToggleIcon = ArrowDownIcon,
            toggleProps,
            isLoading
        } = this.props;
        const { autoFocus } = this.state;

        let displayText;
        let value = valueProps !== undefined ? valueProps : defaultValue;

        if (multiple) {
            displayText = [];
            value = Array.isArray(valueProps) ? [...valueProps] : [defaultValue];
        }

        const isOpen = this.isOpenControlled && this.anchorEl.current ? open : this.state.isOpen;
        const rootClasses = classNames('onsolve-dropdown', className, classes.root);
        const displayTextClasses = classNames(
            {
                'onsolve-dropdown__text--link': variant === 'link'
            },
            classes.displayText
        );

        const items = _.map(data, (item, index) => {
            const text = getTextFromItem(item, textField);
            const id = getDataKeyItem(item, dataItemKey);

            let selected = false;

            if (multiple && _.some(value, v => v === id)) {
                selected = true;
                displayText.push(text);
            } else if (value === id) {
                selected = true;
                displayText = text;
            }

            return this.renderItem(index, item, selected, multiple);
        });

        const ariaLabel = this.props['aria-label'] || label;

        let ariaProps = {
            'aria-selected': !_.isEmpty(displayText),
            'aria-expanded': isOpen,
            'aria-owns': this.listBoxId,
            'aria-haspopup': 'listbox',
            role: 'combobox'
        };

        let ariaPropsMenu = {
            role: 'listbox'
        };

        if (ariaLabel?.length) {
            ariaProps['aria-label'] = ariaPropsMenu['aria-label'] = ariaLabel;
        }

        return (
            <>
                <InputFieldControl className={classNames(rootClasses, classes)}>
                    <InputFieldGroup>
                        {label && <InputFieldLabel>{label}</InputFieldLabel>}
                        <InputFieldContainer
                            {...ariaProps}
                            tabIndex={tabIndex}
                            variant={variant === 'link' ? 'naked' : 'standard'}
                            error={error}
                            active={isOpen}
                            ref={ReactUtils.setMultipleRefs(this.anchorEl, innerRef)}
                            onKeyDown={this.handleKeyDown}
                            onKeyUp={this.handleKeyUp}
                        >
                            <DropdownInput
                                name={name}
                                className={displayTextClasses}
                                value={this.renderValue(displayText)}
                                placeholder={placeholder}
                                disabled={disabled}
                                onClick={this.handleToggle}
                            />
                            {showToggle && (
                                <InputFieldToggle
                                    icon={ToggleIcon}
                                    disabled={disabled}
                                    onClick={this.handleToggle}
                                    {...toggleProps}
                                />
                            )}
                        </InputFieldContainer>
                    </InputFieldGroup>
                    <InputFieldMessage
                        error={error}
                        errorText={errorText}
                        helpText={helpText}
                        currentLength={currentLength}
                        maxLength={maxLength}
                    />
                </InputFieldControl>
                <Menu
                    id={this.listBoxId}
                    anchorEl={this.anchorEl.current}
                    autoFocus={autoFocus}
                    styles={styles.scrollbar}
                    open={isOpen}
                    {...ariaPropsMenu}
                    {...menuProps}
                    isLoading={isLoading}
                    onClose={this.handleMenuClose}
                    onKeyDown={this.handleMenuKeyDown}
                    onKeyUp={this.handleMenuKeyUp}
                >
                    {items}
                </Menu>
            </>
        );
    }
}

Dropdown.propTypes = {
    /**
    Specifies an aria-label attribute for the dropdown to implement WCAG requirements.
    */
    'aria-label': PropTypes.string,
    /**
     Pass class to wrapper component
     */
    className: PropTypes.string,
    /**
     Pass classes to child components
     */
    classes: PropTypes.object.isRequired,
    /**
     The current length of user input text.
     */
    currentLength: PropTypes.number,
    /**
     Data
     */
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.arrayOf(Object)]).isRequired,
    /**
     Key to extract value from object
     */
    dataItemKey: PropTypes.string,
    /**
     Default value
     */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     Disable interaction with component
     */
    disabled: PropTypes.bool,
    /**
     Put input to error state
     */
    error: PropTypes.bool,
    /**
     Error text
     */
    errorText: PropTypes.node,
    /**
     Add secondary text
     */
    helpText: PropTypes.node,
    /**
     Id
     */
    id: PropTypes.string,
    /**
     Pass reference to input dom element
     */
    innerRef: PropTypes.object,
    /**
     Show/hide loading indicator in dropdown menu.
     */
    isLoading: PropTypes.bool,
    /**
     Render function for menu item.
     */
    itemRender: PropTypes.func,
    /**
     Render function for item text.
     */
    itemTextRender: PropTypes.func,
    /**
     Label.
     */
    label: PropTypes.node,
    /**
     Specifies a max length value.
     */
    maxLength: PropTypes.number,
    /**
     Menu props.
     */
    menuProps: PropTypes.shape({
        position: PropTypes.oneOf(['left', 'right', 'center'])
    }),
    /**
     Enable/disable multiple selection.
     */
    multiple: PropTypes.bool,
    /**
     Handler function .
     */
    name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     Handler function for selection.
     */
    onChange: PropTypes.func,
    /**
     Handler function for click event.
     */
    onClick: PropTypes.func,
    /**
     Handler function for close event.
     */
    onClose: PropTypes.func,
    /**
     Handler function for keydown event.
     */
    onKeyDown: PropTypes.func,
    /**
     Control menu open state.
     */
    open: PropTypes.bool,
    /**
     Placeholder text.
     */
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     Show toggle.
     */
    showToggle: PropTypes.bool,
    /**
     Styles object.
     */
    styles: PropTypes.object,
    /**
     Tab index property.
     */
    tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     Text field value.
     */
    textField: PropTypes.string,
    /**
     Toggle icon.
     */
    toggleIcon: PropTypes.func,
    /**
     Toggle properties.
     */
    toggleProps: PropTypes.shape({
        hoverColor: PropTypes.string,
        defaultColor: PropTypes.string
    }),
    /**
     Value.
     */
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    ]),
    /**
     Value render function.
     */
    valueRender: PropTypes.func,
    /**
     Variant.
     */
    variant: PropTypes.oneOf(['standard', 'link', 'button']).isRequired
};

Dropdown.defaultProps = {
    id: 'dropdown',
    classes: {},
    dataItemKey: 'id',
    showToggle: true,
    menuProps: {
        position: 'center'
    },
    styles: {},
    tabIndex: '0',
    textField: 'value',
    variant: 'standard'
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
